import {
  Box,
  IconButton,
  Tooltip,
  useDisclosure,
  Icon,
} from '@chakra-ui/react';
import { useRef } from 'react';
import { FormBuilder } from '../../FormBuilder';
import * as Yup from 'yup';
import { Popover } from '@/components/ats';
import { colors } from '@/theme/foundations/colors';
import { useKFlags } from '@/hooks/use-k-flags';
import { Link01, LinkBroken01 } from '@kula-ai/icons-core';

// Improved regex to validate URLs, including paths
const URL_REGEX =
  /^(https?:\/\/[^\s.]+\.\S{2,}(\/\S*)?|[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}(\/\S*)?)$/i;

const formatInput = (input) => {
  // If it's a domain, prepend https:// if no scheme is present
  if (
    !/^https?:\/\//i.test(input) &&
    /^[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/.test(input)
  ) {
    return `https://${input}`;
  }
  return input; // Return the input as is for other cases
};

export const EditorLink = ({ editor, isTextSelected, color, isActive }) => {
  const formRef = useRef(null);
  const { ats_enabled } = useKFlags();
  const popoverActions = useDisclosure();

  const handleOnInsert = (values, onClose) => {
    const { url } = values || {};
    const formattedUrl = formatInput(url);

    // Validate against the URL_REGEX
    if (URL_REGEX.test(formattedUrl)) {
      editor
        .chain()
        .focus()
        .extendMarkRange('link')
        .setLink({ href: formattedUrl })
        .run();
    } else {
      // Handle invalid URL (e.g., show error message)
      console.error('Invalid URL or link:', formattedUrl);
    }
    onClose?.();
  };

  const handleOnUnlink = ({ popoverDisclosure }) => {
    editor.chain().focus().unsetLink().run();
    popoverDisclosure?.onClose();
  };
  const previousUrl = editor.getAttributes('link').href || '';

  const linkConfig = [
    {
      name: 'url',
      label: 'URL',
      isRequired: true,
      isInputSubmit: true,
      type: 'input',
      validation: Yup.string()
        .matches(URL_REGEX, {
          message: 'Please enter a valid URL',
          excludeEmptyString: true,
        })
        .required('URL is required'),
      autoFocus: true,
    },
  ];

  const renderLinkForm = (onClose = () => {}) => (
    <Box h="100%" w="100%" overflowY="auto" overflowX="hidden" p="10px">
      <FormBuilder
        initialValues={{ url: formatInput(previousUrl) }}
        config={linkConfig}
        onSubmit={(val) => handleOnInsert(val, onClose)}
        formRef={formRef}
      />
    </Box>
  );

  const triggerElement = (
    <Box>
      <Tooltip
        label={isActive ? 'Edit or Unlink' : 'Link'}
        variant={ats_enabled && 'primary'}
        arrowSize={8}
        hasArrow
        placement="top"
        borderRadius="4px"
      >
        <IconButton
          aria-label={isActive ? 'Unlink' : 'Link'}
          height="auto"
          _hover={{ bg: 'inherit' }}
          borderRadius="6px"
          cursor="pointer"
          opacity={isTextSelected || isActive ? 1 : 0.5}
          pointerEvents={isTextSelected || isActive ? 'auto' : 'none'}
          py="8px"
          px="0px"
          color={color}
          bg={isActive ? '#e0e0e0' : 'transparent'}
          data-test-id="editor-toolbar-link-button"
          onClick={() => {
            if (isActive) {
              formRef.current.setValues({ url: formatInput(previousUrl) });
            }
            // else if (formRef?.current) {
            //   formRef.current.submitForm();
            // }
          }}
        >
          {isActive ? (
            <Icon
              // @ts-expect-error - icon is a valid IconType
              as={LinkBroken01}
              color={colors['secondary-700']}
              size="16px"
            />
          ) : (
            // @ts-expect-error - icon is a valid IconType
            <Icon as={Link01} color={colors['secondary-700']} size="16px" />
          )}
        </IconButton>
      </Tooltip>
    </Box>
  );

  if (!isTextSelected && !isActive) return triggerElement;

  return (
    <Popover
      popoverActions={popoverActions}
      triggerElement={triggerElement}
      popoverProps={{
        closeOnBlur: false,
        placement: 'top-end',
      }}
      secondaryCtaLabel={isActive ? 'Remove link' : null}
      secondaryCtaProps={{
        disabled: !isActive,
      }}
      secondaryCtaOnClick={handleOnUnlink}
      ctaLabel={isActive ? 'Update link' : 'Insert link'}
      ctaOnClick={() => {
        if (formRef?.current) {
          formRef.current.submitForm();
        }
      }}
      popoverContentProps={{
        onClick: (e) => e.stopPropagation(),
      }}
    >
      {renderLinkForm()}
    </Popover>
  );
};
